<template>
	<rmp-sign-in-form-base :title="title">
		<v-responsive class="py-2" v-if="mode === AuthPageModeType.INITIAL">
			<v-form :readonly="formSaving || formReadonly" :disabled="formDisabled"
					@submit.prevent="submit"
					ref="form" v-model="formValid">
				<v-row>
					<v-col class="pt-1 pb-2">
						<rmp-text-field autocomplete="new-password"
										name="password"
										@click:append="isPasswordHidden = !isPasswordHidden"
										:type="isPasswordHidden ? 'password' : 'text'"
										:append-icon="isPasswordHidden ? 'mdi-eye-off' : 'mdi-eye'"
										:placeholder="tc('fields.newPassword.placeholder')"
										:label="tc('fields.newPassword.label')"
										class="mb-2"
										:rules="validation.password"
										clearable
										v-model="password">
						</rmp-text-field>
						<rmp-text-field autocomplete="new-password"
										@click:append="isConfirmedPasswordHidden = !isConfirmedPasswordHidden"
										:type="isConfirmedPasswordHidden ? 'password' : 'text'"
										:append-icon="isConfirmedPasswordHidden ? 'mdi-eye-off' : 'mdi-eye'"
										:placeholder="tc('fields.confirmedPassword.placeholder')"
										:label="tc('fields.confirmedPassword.label')"
										:rules="validation.confirmedPassword"
										clearable
										v-model="confirmedPassword">
						</rmp-text-field>
					</v-col>
				</v-row>
				<v-row class="flex-column">
					<v-col class="pt-0">
						<rmp-btn class="rmp-btn"
								 type="submit"
								 color="primary"
								 :loading="formSaving"
								 :disabled="!formValid"
								 block>
							{{ tc("buttons.confirm") }}
						</rmp-btn>
						
						<rmp-btn color="secondary"
								 :to="{ name: RouteNames.SIGN_IN }"
								 :disabled="formSaving"
								 block
								 large
								 outlined
								 class="rmp-btn mt-4">
							{{ tc("buttons.toSignIn") }}
						</rmp-btn>
					</v-col>
				</v-row>
			</v-form>
		</v-responsive>

		<div v-if="mode === AuthPageModeType.SUCCESS" class="d-flex flex-column">
			<rmp-text tag="span" type="body-large" :color="colors.grey.darken1" class="mb-4">
				{{ t("common.resetPasswordFormText.successText", { seconds: formatSecondsLeft(store.secondsUntilRedirectToAuth) }).toString()}}
			</rmp-text>
			
			<rmp-btn color="secondary"
					 :to="{ name: RouteNames.SIGN_IN }"
					 outlined>
				{{ tc("buttons.toSignIn") }}
			</rmp-btn>
		</div>
	</rmp-sign-in-form-base>
</template>

<script setup lang="ts">
import RmpText from "@rmp/core/components/typography/RmpText.vue";
import RmpBtn from "@rmp/core/components/buttons/RmpBtn.vue";
import RmpTextField from "@rmp/core/components/fields/RmpTextField.vue";
import RmpSignInFormBase from "@rmp/core/components/form/RmpSignInFormBase.vue";
import { useI18n } from "vue-i18n-composable";
import { useRoute } from "vue-router/composables";
import { useColors } from "@rmp/core/composables/useColors";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useForm } from "@rmp/core/composables/useForm";
import { usePage } from "@rmp/core/composables/usePage";
import { useResetPasswordStore } from "@rmp/id/stores/resetPassword";
import { prepareConfirmedPasswordRule, preparePasswordRule, requiredRule } from "@rmp/core/utils/validation";
import { AuthPageModeType } from "@rmp/core/types/authorization/authPageModeType";
import { formatSecondsLeft } from "@rmp/core/utils/dates";
import { RouteNames } from "@rmp/id/router/routes";

const { tc, t } = useI18n();
const route = useRoute();
const { colors } = useColors();

const useStore = useResetPasswordStore;
const store = useStore();
const { password, mode } = storeToRefs(store);

const title = computed(() => {
	switch (mode.value) {
		case AuthPageModeType.INITIAL:
			return tc("titles.resetPassword");
		case AuthPageModeType.SUCCESS:
			return tc("titles.resetPasswordSuccess");
	}
});

const isPasswordHidden = ref(true);
const confirmedPassword = ref("");
const isConfirmedPasswordHidden = ref(true);

const validation = {
	login: [requiredRule()],
	password: [requiredRule(), preparePasswordRule()],
	confirmedPassword: [requiredRule(), prepareConfirmedPasswordRule(() => password.value)]
};

const {
	form,
	formStateLoading,
	formReadonly,
	formDisabled,
	formSaving,
	formValid,
	submit
} = useForm({ useStore }, true);

const { initialized, loading } = usePage({ useStore });
</script>
